import { Add, ArrowDownward, ArrowUpward, DeleteOutline } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, IconButton, MenuItem, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { ChangeEvent, JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { absoluteBrandingUrl, useBranding } from '../../../contexts/BrandingContext';
import { CONTENT_ICON_NAMES, contentIconTile } from '../../../library/contentIcons';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { clinicHomeContent } from '../../../library/models/homeContent';
import { HomeCardDraft, HomeDraft, ITenantHome, emptyHomeCard, toHomeDraft, toPreviewHome, toTenantHomeRequest } from '../../../library/models/tenantHome';
import { splitApiError } from '../../../library/useApiFormError';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { LandingPageView } from '../LandingPage/LandingPageView';
import { LogoCropDialog } from '../../imageCrop/LogoCropDialog';
import { actionsStyles, addCardStyles, bilingualRowStyles, blockedSaveStyles, cardHintStyles, cardRowHeaderStyles, cardRowStyles, cardRowTitleStyles, cardStyles, cardTitleStyles, heroEmptyStyles, heroRowStyles, heroThumbStyles, iconOptionStyles, iconPickerStyles, layoutStyles, previewColumnStyles, previewFrameStyles, previewHeaderStyles, previewHintStyles, previewScalerStyles, sectionStyles } from './homeSettings.styles';
import { createHomeSchema } from './homeSchema';

/**
 * What the clinic's own front page says.
 *
 * A tab rather than an edit mode on `/home` itself, unlike About and Our Services. Those pages
 * change when their admin signs in; this one deliberately does not -- `/home` is a shopfront
 * decided by the hostname, and a patient who signs in should not watch the page they were
 * reading sprout an Edit button. The console is also the only place the question "which
 * clinic" has an answer while an admin sits on the platform's own address.
 *
 * <h2>The fields are the page</h2>
 *
 * Every clinic's page is stored whole, starting from the starter copy the platform writes when
 * the clinic is created, so the form opens on exactly the words patients read. There is no
 * hidden default behind an empty box: clearing one takes that line off the page, and each
 * field's hint says so. The first headline line is the one that cannot be cleared.
 *
 * <h2>The preview is the page</h2>
 *
 * Not a mock-up of it: {@link LandingPageView} rendered from the draft through
 * {@link clinicHomeContent}, the same component and the same resolver a patient gets. So a
 * line cleared here disappears from the preview exactly as it will disappear in public.
 *
 * <h2>The photograph is not part of the draft</h2>
 *
 * It uploads the moment it is cropped, through a route of its own, and Save never touches it.
 * That is the logo's arrangement in the branding tab and it holds for the same two reasons: a
 * multipart body has no business inside a JSON save, and the two are stored by statements that
 * name different columns, so neither can blank the other.
 */
export const HomeSettings = (): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { logoAlt: clinicName } = useBranding();

    const { data, isLoading, refetch } = useReactQuery<ITenantHome>(
        services.tenantHome.getTenantHome()
    );

    const saveHome = useReactMutation(services.tenantHome.saveTenantHomeMutation());
    const uploadHero = useReactMutation(services.tenantHome.uploadHeroMutation());
    const removeHero = useReactMutation(services.tenantHome.removeHeroMutation());

    const schema = useMemo(() => createHomeSchema(translate), [translate]);

    const {
        control, register, handleSubmit, reset, setError,
        formState: { errors, isValid },
    } = useValidatedForm<HomeDraft>(schema, { defaultValues: toHomeDraft(undefined) });

    const { fields, append, remove, move } = useFieldArray<HomeDraft, 'cards', 'key'>({
        control,
        name: 'cards',
        // The draft's own key, not react-hook-form's generated `id`: a card carries one from
        // the moment it is created, and two identities for one row is one too many.
        keyName: 'key',
    });

    /** Reseeded from the server, so the fields open on what is actually published. */
    useEffect(() => {
        if (data) {
            reset(toHomeDraft(data));
        }
    }, [data, reset]);

    const draft: HomeDraft = useWatch({ control }) as HomeDraft;

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);

    const [pickedFile, setPickedFile] = useState<File | null>(null);
    const fileInput = useRef<HTMLInputElement | null>(null);

    const heroUrl: string | null = absoluteBrandingUrl(data?.heroImageUrl, data?.heroImageEtag);

    const onValid = (values: HomeDraft): void => {
        saveHome.mutate(toTenantHomeRequest(values), {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.HOME_SETTINGS_SAVE_SUCCESS), 'success');
                refetch();
            },
            onError: (error: Error) => {
                const outcome = splitApiError(error, knownFields, translate,
                    ResourcesIds.HOME_SETTINGS_SAVE_ERROR);

                Object.entries(outcome.fieldErrors).forEach(([field, message]) =>
                    setError(field as keyof HomeDraft, { type: 'server', message }));

                if (outcome.formError) {
                    showSnackbar(outcome.formError, 'error');
                }
            },
        });
    };

    const onPick = (event: ChangeEvent<HTMLInputElement>): void => {
        const picked: File | undefined = event.target.files?.[0];

        // Cleared straight away, or picking the same file twice in a row fires no change event
        // and the dialog never reopens.
        event.target.value = '';

        if (picked) {
            setPickedFile(picked);
        }
    };

    const onCropped = (cropped: File): void => {
        uploadHero.mutate(cropped, {
            onSuccess: () => {
                setPickedFile(null);
                showSnackbar(translate(ResourcesIds.HOME_HERO_UPLOAD_SUCCESS), 'success');
                refetch();
            },
            onError: () => showSnackbar(translate(ResourcesIds.HOME_HERO_UPLOAD_ERROR), 'error'),
        });
    };

    const onRemoveHero = (): void => {
        removeHero.mutate(undefined, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.HOME_HERO_REMOVE_SUCCESS), 'success');
                refetch();
            },
            onError: () => showSnackbar(translate(ResourcesIds.HOME_HERO_REMOVE_ERROR), 'error'),
        });
    };

    if (isLoading) {
        return (
            <Box sx={sectionStyles}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={sectionStyles}>
            <Box sx={layoutStyles}>
                <Box component="form" onSubmit={handleSubmit(onValid)} noValidate>
                    {/* The hero */}
                    <Paper sx={cardStyles} elevation={0}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                            {translate(ResourcesIds.HOME_SETTINGS_HERO_TITLE)}
                        </Typography>
                        <Typography variant="body2" sx={cardHintStyles}>
                            {translate(ResourcesIds.HOME_SETTINGS_HERO_HINT)}
                        </Typography>

                        {bilingualPair('heroEyebrowRo', 'heroEyebrowEn',
                            ResourcesIds.HOME_FIELD_EYEBROW_RO, ResourcesIds.HOME_FIELD_EYEBROW_EN,
                            register, errors, translate)}
                        {bilingualPair('heroHeadline1Ro', 'heroHeadline1En',
                            ResourcesIds.HOME_FIELD_HEADLINE1_RO, ResourcesIds.HOME_FIELD_HEADLINE1_EN,
                            register, errors, translate, false, true)}
                        {bilingualPair('heroHeadline2Ro', 'heroHeadline2En',
                            ResourcesIds.HOME_FIELD_HEADLINE2_RO, ResourcesIds.HOME_FIELD_HEADLINE2_EN,
                            register, errors, translate)}
                        {bilingualPair('heroSubtitleRo', 'heroSubtitleEn',
                            ResourcesIds.HOME_FIELD_SUBTITLE_RO, ResourcesIds.HOME_FIELD_SUBTITLE_EN,
                            register, errors, translate, true)}

                        <Typography variant="body2" sx={cardHintStyles}>
                            {translate(ResourcesIds.HOME_HERO_IMAGE_HINT)}
                        </Typography>

                        <Box sx={heroRowStyles}>
                            {heroUrl ? (
                                <Box component="img" src={heroUrl} alt="" sx={heroThumbStyles} />
                            ) : (
                                <Box sx={heroEmptyStyles}>
                                    <Typography variant="caption">
                                        {translate(ResourcesIds.HOME_HERO_IMAGE_NONE)}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={actionsStyles}>
                                {/* Wider than the wire on purpose, exactly as the logo's picker
                                    is: the crop dialog re-encodes every pick through a canvas,
                                    so what reaches the API is a JPEG whatever was chosen. */}
                                <input
                                    ref={fileInput}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    hidden
                                    onChange={onPick}
                                    data-testid="hero-file-input"
                                />

                                <Button
                                    variant="outlined"
                                    disabled={uploadHero.isPending}
                                    onClick={() => fileInput.current?.click()}
                                >
                                    {translate(heroUrl
                                        ? ResourcesIds.HOME_HERO_IMAGE_REPLACE
                                        : ResourcesIds.HOME_HERO_IMAGE_UPLOAD)}
                                </Button>

                                {heroUrl && (
                                    <Button
                                        color="inherit"
                                        disabled={removeHero.isPending}
                                        onClick={onRemoveHero}
                                    >
                                        {translate(ResourcesIds.HOME_HERO_IMAGE_REMOVE)}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Paper>

                    {/* The band of cards */}
                    <Paper sx={cardStyles} elevation={0}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                            {translate(ResourcesIds.HOME_SETTINGS_FEATURES_TITLE)}
                        </Typography>
                        <Typography variant="body2" sx={cardHintStyles}>
                            {translate(ResourcesIds.HOME_SETTINGS_FEATURES_HINT)}
                        </Typography>

                        {bilingualPair('featuresEyebrowRo', 'featuresEyebrowEn',
                            ResourcesIds.HOME_FIELD_EYEBROW_RO, ResourcesIds.HOME_FIELD_EYEBROW_EN,
                            register, errors, translate)}
                        {bilingualPair('featuresTitleRo', 'featuresTitleEn',
                            ResourcesIds.HOME_FIELD_SECTION_TITLE_RO, ResourcesIds.HOME_FIELD_SECTION_TITLE_EN,
                            register, errors, translate)}
                        {bilingualPair('featuresSubtitleRo', 'featuresSubtitleEn',
                            ResourcesIds.HOME_FIELD_SECTION_SUBTITLE_RO, ResourcesIds.HOME_FIELD_SECTION_SUBTITLE_EN,
                            register, errors, translate, true)}

                        {fields.length === 0 && (
                            <Alert severity="info" sx={cardHintStyles}>
                                {translate(ResourcesIds.HOME_CARDS_EMPTY_HINT)}
                            </Alert>
                        )}

                        {fields.map((field: HomeCardDraft, index: number) => (
                            <Box key={field.key} sx={cardRowStyles}>
                                <Box sx={cardRowHeaderStyles}>
                                    <Typography variant="subtitle2" sx={cardRowTitleStyles}>
                                        {translate(ResourcesIds.HOME_CARD_NUMBER, { number: index + 1 })}
                                    </Typography>

                                    <Tooltip title={translate(ResourcesIds.HOME_CARD_MOVE_UP)}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={index === 0}
                                                onClick={() => move(index, index - 1)}
                                                aria-label={translate(ResourcesIds.HOME_CARD_MOVE_UP)}
                                            >
                                                <ArrowUpward fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title={translate(ResourcesIds.HOME_CARD_MOVE_DOWN)}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={index === fields.length - 1}
                                                onClick={() => move(index, index + 1)}
                                                aria-label={translate(ResourcesIds.HOME_CARD_MOVE_DOWN)}
                                            >
                                                <ArrowDownward fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title={translate(ResourcesIds.HOME_CARD_REMOVE)}>
                                        <IconButton
                                            size="small"
                                            onClick={() => remove(index)}
                                            aria-label={translate(ResourcesIds.HOME_CARD_REMOVE)}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Controller
                                    control={control}
                                    name={`cards.${index}.icon` as never}
                                    render={({ field: icon }) => (
                                        <TextField
                                            select
                                            sx={iconPickerStyles}
                                            label={translate(ResourcesIds.HOME_FIELD_CARD_ICON)}
                                            value={icon.value ?? ''}
                                            onChange={icon.onChange}
                                        >
                                            {CONTENT_ICON_NAMES.map((name: string) => (
                                                <MenuItem key={name} value={name}>
                                                    <Box sx={iconOptionStyles}>
                                                        {contentIconTile(name)}
                                                        {name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />

                                <Box sx={bilingualRowStyles}>
                                    <TextField
                                        fullWidth
                                        label={translate(ResourcesIds.HOME_FIELD_CARD_TITLE_RO)}
                                        {...register(`cards.${index}.titleRo` as never)}
                                        error={!!errors.cards?.[index]?.titleRo}
                                        helperText={errors.cards?.[index]?.titleRo?.message}
                                    />
                                    <TextField
                                        fullWidth
                                        label={translate(ResourcesIds.HOME_FIELD_CARD_TITLE_EN)}
                                        {...register(`cards.${index}.titleEn` as never)}
                                        error={!!errors.cards?.[index]?.titleEn}
                                        helperText={errors.cards?.[index]?.titleEn?.message}
                                    />
                                </Box>

                                <Box sx={bilingualRowStyles}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        label={translate(ResourcesIds.HOME_FIELD_CARD_BODY_RO)}
                                        {...register(`cards.${index}.bodyRo` as never)}
                                        error={!!errors.cards?.[index]?.bodyRo}
                                        helperText={errors.cards?.[index]?.bodyRo?.message}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        label={translate(ResourcesIds.HOME_FIELD_CARD_BODY_EN)}
                                        {...register(`cards.${index}.bodyEn` as never)}
                                        error={!!errors.cards?.[index]?.bodyEn}
                                        helperText={errors.cards?.[index]?.bodyEn?.message}
                                    />
                                </Box>
                            </Box>
                        ))}

                        <Button
                            startIcon={<Add />}
                            sx={addCardStyles}
                            onClick={() => append(emptyHomeCard())}
                        >
                            {translate(ResourcesIds.HOME_CARD_ADD)}
                        </Button>
                    </Paper>

                    {/* The closing call to action */}
                    <Paper sx={cardStyles} elevation={0}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                            {translate(ResourcesIds.HOME_SETTINGS_CTA_TITLE)}
                        </Typography>
                        <Typography variant="body2" sx={cardHintStyles}>
                            {translate(ResourcesIds.HOME_SETTINGS_CTA_HINT)}
                        </Typography>

                        {bilingualPair('ctaTitleRo', 'ctaTitleEn',
                            ResourcesIds.HOME_FIELD_CTA_TITLE_RO, ResourcesIds.HOME_FIELD_CTA_TITLE_EN,
                            register, errors, translate)}
                        {bilingualPair('ctaSubtitleRo', 'ctaSubtitleEn',
                            ResourcesIds.HOME_FIELD_CTA_SUBTITLE_RO, ResourcesIds.HOME_FIELD_CTA_SUBTITLE_EN,
                            register, errors, translate, true)}
                    </Paper>

                    <Box sx={actionsStyles}>
                        <Button type="submit" variant="contained" disabled={!isValid || saveHome.isPending}>
                            {translate(ResourcesIds.HOME_SETTINGS_SAVE)}
                        </Button>

                        {/* Never a greyed button with no explanation beside it. */}
                        {!isValid && (
                            <Typography variant="body2" sx={blockedSaveStyles}>
                                {translate(ResourcesIds.HOME_SETTINGS_BLOCKED_SAVE)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* The preview */}
                <Box sx={previewColumnStyles}>
                    <Box sx={previewHeaderStyles}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                            {translate(ResourcesIds.HOME_PREVIEW_TITLE)}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={previewHintStyles}>
                        {translate(ResourcesIds.HOME_PREVIEW_HINT)}
                    </Typography>

                    <Box sx={previewFrameStyles}>
                        <Box sx={previewScalerStyles} data-testid="home-preview">
                            <LandingPageView
                                content={clinicHomeContent(
                                    toPreviewHome(draft, data?.heroImageUrl ?? null,
                                        data?.heroImageEtag ?? null),
                                    i18n.language,
                                    clinicName,
                                    heroUrl
                                )}
                                /* Signed out, always: this is what a patient arriving at the
                                   clinic's address sees, and it is what the admin is deciding
                                   about. Their own session is beside the point here. */
                                isAuthenticated={false}
                                onPrimaryAction={noop}
                                onSignIn={noop}
                                onLearnMore={noop}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <LogoCropDialog
                file={pickedFile}
                variant="hero"
                isUploading={uploadHero.isPending}
                onCancel={() => setPickedFile(null)}
                onConfirm={onCropped}
            />
        </Box>
    );
};

/** The preview's buttons are drawn because the page has them, and do nothing because it is one. */
const noop = (): void => undefined;

/**
 * One line of copy in both languages.
 *
 * A function rather than a component so the fields stay uncontrolled `register` spreads --
 * a component would remount on every keystroke of its parent and lose the caret.
 *
 * The hints say what a blank box does, which differs by side: a blank Romanian line is off the
 * page, a blank English one shows the Romanian to English readers. A `required` line gets no
 * hint on its Romanian side, since it cannot be left blank at all.
 */
const bilingualPair = (
    nameRo: keyof HomeDraft,
    nameEn: keyof HomeDraft,
    labelRo: string,
    labelEn: string,
    register: ReturnType<typeof useValidatedForm<HomeDraft>>['register'],
    errors: ReturnType<typeof useValidatedForm<HomeDraft>>['formState']['errors'],
    translate: (key: string, options?: object) => string,
    multiline: boolean = false,
    required: boolean = false
): JSX.Element => (
    <Box sx={bilingualRowStyles}>
        <TextField
            fullWidth
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            label={translate(labelRo)}
            required={required}
            {...register(nameRo as never)}
            {...fieldProps(errors, nameRo as never, required ? undefined : translate(ResourcesIds.HOME_FIELD_HIDE_HINT))}
        />
        <TextField
            fullWidth
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            label={translate(labelEn)}
            {...register(nameEn as never)}
            {...fieldProps(errors, nameEn as never, translate(ResourcesIds.HOME_FIELD_EN_HINT))}
        />
    </Box>
);
